import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Autocomplete,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon, Lock as LockIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import Header from '../../components/Layout/Header';
import Sidebar from '../../components/Layout/Sidebar';
import { classService, examService, subjectService, studentService, markService } from '../../services/apiService';
import { showToast } from '../../utils/toast';
import { useAuth } from '../../contexts/AuthContext';

const AddResults = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sidebarOpen, setSidebarOpen] = useState(!isMobile);
  const drawerWidth = 280;
  const { user, isTeacher, isAdmin } = useAuth();

  const [selectedClass, setSelectedClass] = useState('');
  const [selectedExam, setSelectedExam] = useState('');
  const [defaultMaxMarks, setDefaultMaxMarks] = useState('100');
  const [classes, setClasses] = useState([]);
  const [exams, setExams] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMarks, setLoadingMarks] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  // Store validation errors: { studentId: { subjectId: { marks: errorMessage, maxMarks: errorMessage } } }
  const [validationErrors, setValidationErrors] = useState({});

  // Store marks: { studentId: { subjectId: { marks, maxMarks } } }
  const [marksData, setMarksData] = useState({});
  
  // Store max marks per subject: { subjectId: maxMarks }
  const [subjectMaxMarks, setSubjectMaxMarks] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [classesRes, examsRes] = await Promise.all([
          classService.getAll(),
          examService.getAll(),
        ]);
        let classesData = classesRes?.data || classesRes || [];
        const examsData = examsRes?.data || examsRes || [];
        
        // For teachers, filter classes to only show those where they teach at least one subject
        if (isTeacher() && !isAdmin()) {
          const userId = user?.id || user?.user_id;
          // First fetch all subjects for this teacher
          try {
            const subjectsRes = await subjectService.getAll({ teacher_id: userId });
            const teacherSubjects = subjectsRes?.data || subjectsRes || [];
            const teacherClassIds = [...new Set(teacherSubjects.map(s => s.class_id))];
            classesData = classesData.filter(c => teacherClassIds.includes(c.id));
          } catch (err) {
            console.error('Failed to fetch teacher subjects:', err);
          }
        }
        
        setClasses(Array.isArray(classesData) ? classesData : []);
        setExams(Array.isArray(examsData) ? examsData : []);
      } catch (err) {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isTeacher, isAdmin, user]);

  // Load students when class is selected
  useEffect(() => {
    const fetchStudents = async () => {
      if (!selectedClass) {
        setStudents([]);
        setSubjects([]);
        setMarksData({});
        setSubjectMaxMarks({});
        setValidationErrors({});
        return;
      }

      try {
        setLoading(true);
        const studentsRes = await studentService.getAll();
        const studentsData = (studentsRes?.data || studentsRes || []).filter(
          (s) => s.class_id === selectedClass
        );
        setStudents(Array.isArray(studentsData) ? studentsData : []);
      } catch (err) {
        setError('Failed to load students');
        showToast.error('Failed to load students');
      } finally {
        setLoading(false);
      }
    };

    fetchStudents();
  }, [selectedClass]);

  // Clear subjects and marks when class or exam changes (but don't auto-load)
  useEffect(() => {
    if (!selectedClass || !selectedExam) {
      setSubjects([]);
      setMarksData({});
      setSubjectMaxMarks({});
      setValidationErrors({});
    }
  }, [selectedClass, selectedExam]);

  // Function to load subjects and marks sequentially when Load Marks button is clicked
  const fetchExistingMarks = async () => {
    if (!selectedClass || !selectedExam) {
      showToast.error('Please select both Class and Exam');
      return;
    }

    if (students.length === 0) {
      showToast.error('Please wait for students to load');
      return;
    }

    try {
      setLoadingMarks(true);
      
      // Step 1: Load subjects first
      const subjectsRes = await subjectService.getAll({ class_id: selectedClass });
      let subjectsData = subjectsRes?.data || subjectsRes || [];
      
      // For teachers, prioritize subjects where isRestricted is false (assigned subjects)
      if (isTeacher() && !isAdmin()) {
        const userId = user?.id || user?.user_id;
        subjectsData = subjectsData.sort((a, b) => {
          // Check if teacher is assigned to each subject
          const aIsAssigned = a.teacher_id === userId;
          const bIsAssigned = b.teacher_id === userId;
          
          // Subjects where isRestricted is false (assigned) should come first
          if (aIsAssigned && !bIsAssigned) return -1; // a comes first
          if (!aIsAssigned && bIsAssigned) return 1;  // b comes first
          
          // If both have same restriction status, sort alphabetically
          return (a.name || '').localeCompare(b.name || '');
        });
      }
      
      setSubjects(Array.isArray(subjectsData) ? subjectsData : []);

      if (subjectsData.length === 0) {
        showToast.warning('No subjects found for this class');
        setLoadingMarks(false);
        return;
      }

      // Initialize subject max marks
      const initialSubjectMaxMarks = {};
      subjectsData.forEach((subject) => {
        initialSubjectMaxMarks[subject.id] = defaultMaxMarks;
      });
      setSubjectMaxMarks(initialSubjectMaxMarks);

      // Initialize marks data structure
      const initialMarks = {};
      const initialErrors = {};
      students.forEach((student) => {
        initialMarks[student.id] = {};
        initialErrors[student.id] = {};
        subjectsData.forEach((subject) => {
          initialMarks[student.id][subject.id] = { marks: '', maxMarks: initialSubjectMaxMarks[subject.id] };
          initialErrors[student.id][subject.id] = { marks: '', maxMarks: '' };
        });
      });
      setMarksData(initialMarks);
      setValidationErrors(initialErrors);

      // Step 2: Load marks after subjects are loaded
      try {
        const marksRes = await markService.getAll({
          exam_id: selectedExam,
          class_id: selectedClass,
        });

        const marksDataFromAPI = marksRes?.data || marksRes || [];
        
        // Update marksData with existing marks
        if (Array.isArray(marksDataFromAPI) && marksDataFromAPI.length > 0) {
          // First, collect all unique max marks per subject from the API
          const subjectMaxMarksFromAPI = {};
          
          marksDataFromAPI.forEach((studentRecord) => {
            if (studentRecord.subjects) {
              studentRecord.subjects.forEach((subjectRecord) => {
                const subjectId = subjectRecord.id || subjectRecord.subject_id;
                if (subjectRecord.max_marks !== null && subjectRecord.max_marks !== undefined) {
                  subjectMaxMarksFromAPI[subjectId] = subjectRecord.max_marks.toString();
                }
              });
            } else if (studentRecord.subject_id && studentRecord.max_marks !== null && studentRecord.max_marks !== undefined) {
              subjectMaxMarksFromAPI[studentRecord.subject_id] = studentRecord.max_marks.toString();
            }
          });
          
          // Update subjectMaxMarks with values from API
          if (Object.keys(subjectMaxMarksFromAPI).length > 0) {
            setSubjectMaxMarks((prev) => ({
              ...prev,
              ...subjectMaxMarksFromAPI,
            }));
          }
          
          setMarksData((prev) => {
            const updated = { ...prev };
            
            // Process marks data - API returns student objects with nested subjects
            marksDataFromAPI.forEach((studentRecord) => {
              const studentId = studentRecord.id || studentRecord.student_id;
              
              if (updated[studentId] && studentRecord.subjects) {
                // If API returns nested subjects structure
                studentRecord.subjects.forEach((subjectRecord) => {
                  const subjectId = subjectRecord.id || subjectRecord.subject_id;
                  
                  if (updated[studentId][subjectId] !== undefined) {
                    updated[studentId][subjectId] = {
                      marks: subjectRecord.marks !== null && subjectRecord.marks !== undefined 
                        ? subjectRecord.marks.toString() 
                        : '',
                      maxMarks: subjectRecord.max_marks !== null && subjectRecord.max_marks !== undefined
                        ? subjectRecord.max_marks.toString()
                        : (subjectMaxMarksFromAPI[subjectId] || initialSubjectMaxMarks[subjectId] || defaultMaxMarks),
                    };
                  }
                });
              } else if (studentRecord.student_id && studentRecord.subject_id) {
                // If API returns flat structure with student_id and subject_id
                const studentId = studentRecord.student_id;
                const subjectId = studentRecord.subject_id;
                
                if (updated[studentId] && updated[studentId][subjectId] !== undefined) {
                  updated[studentId][subjectId] = {
                    marks: (studentRecord.marks_obtained !== null && studentRecord.marks_obtained !== undefined)
                      ? studentRecord.marks_obtained.toString()
                      : (studentRecord.marks !== null && studentRecord.marks !== undefined)
                      ? studentRecord.marks.toString()
                      : '',
                    maxMarks: (studentRecord.max_marks !== null && studentRecord.max_marks !== undefined)
                      ? studentRecord.max_marks.toString()
                      : (subjectMaxMarksFromAPI[subjectId] || initialSubjectMaxMarks[subjectId] || defaultMaxMarks),
                  };
                }
              }
            });
            
            return updated;
          });
        } else {
          showToast.info('No existing marks found for this exam and class');
        }
      } catch (marksErr) {
        console.error('Failed to load marks:', marksErr);
        showToast.error('Failed to load marks. Please try again.');
      }
    } catch (err) {
      console.error('Failed to load subjects:', err);
      showToast.error('Failed to load subjects. Please try again.');
    } finally {
      setLoadingMarks(false);
    }
  };

  // Update all max marks when defaultMaxMarks changes (only if marksData already exists)
  // Only update max marks that are empty or equal to the previous default
  useEffect(() => {
    if (Object.keys(marksData).length > 0 && selectedClass) {
      setMarksData((prev) => {
        const updated = { ...prev };
        Object.keys(updated).forEach((studentId) => {
          Object.keys(updated[studentId]).forEach((subjectId) => {
            // Only update if maxMarks is empty or matches the default (meaning it wasn't specifically set)
            const currentMax = updated[studentId][subjectId].maxMarks;
            if (!currentMax || currentMax === '100' || currentMax === '') {
              updated[studentId][subjectId] = {
                ...updated[studentId][subjectId],
                maxMarks: defaultMaxMarks,
              };
            }
          });
        });
        return updated;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMaxMarks]);

  const handleMarksChange = (studentId, subjectId, field, value) => {
    // Only allow numbers (including empty string for clearing)
    if (value !== '' && !/^\d+$/.test(value)) {
      // Don't update if it's not a valid number
      return;
    }

    // Update marks data
    setMarksData((prev) => {
      const updated = {
        ...prev,
        [studentId]: {
          ...prev[studentId],
          [subjectId]: {
            ...prev[studentId]?.[subjectId],
            [field]: value,
          },
        },
      };

      // Validate marks
      const marks = field === 'marks' ? value : updated[studentId][subjectId].marks;
      const maxMarks = field === 'maxMarks' ? value : updated[studentId][subjectId].maxMarks;

      // Clear previous errors for this field
      setValidationErrors((prevErrors) => {
        const updatedErrors = { ...prevErrors };
        if (!updatedErrors[studentId]) {
          updatedErrors[studentId] = {};
        }
        if (!updatedErrors[studentId][subjectId]) {
          updatedErrors[studentId][subjectId] = {};
        }
        updatedErrors[studentId][subjectId][field] = '';

        // Validate marks field
        if (field === 'marks' && marks !== '') {
          const marksNum = parseInt(marks, 10);
          if (isNaN(marksNum)) {
            updatedErrors[studentId][subjectId].marks = 'Must be a number';
          } else if (marksNum < 0) {
            updatedErrors[studentId][subjectId].marks = 'Cannot be less than 0';
          } else if (maxMarks && maxMarks !== '') {
            const maxMarksNum = parseInt(maxMarks, 10);
            if (!isNaN(maxMarksNum) && marksNum > maxMarksNum) {
              updatedErrors[studentId][subjectId].marks = `Max allowed ${maxMarksNum}`;
            }
          }
        }

        // Validate maxMarks field
        if (field === 'maxMarks' && maxMarks !== '') {
          const maxMarksNum = parseInt(maxMarks, 10);
          if (isNaN(maxMarksNum)) {
            updatedErrors[studentId][subjectId].maxMarks = 'Must be a number';
          } else if (maxMarksNum <= 0) {
            updatedErrors[studentId][subjectId].maxMarks = 'Must be greater than 0';
          } else if (marks && marks !== '') {
            const marksNum = parseInt(marks, 10);
            if (!isNaN(marksNum) && marksNum > maxMarksNum) {
              updatedErrors[studentId][subjectId].marks = `Max allowed ${maxMarksNum}`;
            }
          }
        }

        return updatedErrors;
      });

      return updated;
    });
  };

  const handleSave = async () => {
    if (!selectedClass || !selectedExam) {
      showToast.error('Please select both Class and Exam');
      return;
    }

    // Check for validation errors
    const hasValidationErrors = Object.values(validationErrors).some((studentErrors) =>
      Object.values(studentErrors).some((subjectErrors) =>
        (subjectErrors.marks && subjectErrors.marks !== '') || (subjectErrors.maxMarks && subjectErrors.maxMarks !== '')
      )
    );

    if (hasValidationErrors) {
      showToast.error('Please fix validation errors before saving');
      return;
    }

    // Validate marks data
    const hasData = Object.values(marksData).some((studentMarks) =>
      Object.values(studentMarks).some((subj) => subj.marks && subj.marks !== '')
    );

    if (!hasData) {
      showToast.error('Please enter marks for at least one student');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      // Format marks data for API
      const userId = user?.id || user?.user_id;
      const marksArray = [];
      Object.keys(marksData).forEach((studentId) => {
        const studentMarks = marksData[studentId];
        Object.keys(studentMarks).forEach((subjectId) => {
          const markData = studentMarks[subjectId];
          // Only include if marks are entered
          if (markData.marks && markData.marks !== '' && markData.marks !== null) {
            // For teachers, check if they are assigned to this subject
            if (isTeacher() && !isAdmin()) {
              const subject = subjects.find(s => s.id === subjectId);
              if (!subject || subject.teacher_id !== userId) {
                // Skip this subject - teacher is not assigned
                return;
              }
            }
            
            const marksObtained = parseInt(markData.marks, 10);
            const maxMarks = parseInt(markData.maxMarks || '100', 10);
            
            // Validate marks
            if (isNaN(marksObtained) || marksObtained < 0) {
              throw new Error(`Invalid marks for student ${studentId}, subject ${subjectId}`);
            }
            if (isNaN(maxMarks) || maxMarks <= 0) {
              throw new Error(`Invalid max marks for student ${studentId}, subject ${subjectId}`);
            }
            if (marksObtained > maxMarks) {
              throw new Error(`Marks obtained (${marksObtained}) cannot be greater than max marks (${maxMarks})`);
            }

            marksArray.push({
              student_id: studentId,
              subject_id: subjectId,
              marks: marksObtained,
              max_marks: maxMarks,
            });
          }
        });
      });

      if (marksArray.length === 0) {
        showToast.error('Please enter marks for at least one student');
        return;
      }

      // Send to API
      await markService.create({
        exam_id: selectedExam,
        marks: marksArray,
      });

      showToast.success('Results saved successfully');
      navigate('/results');
    } catch (err) {
      const errorMessage = err?.response?.data?.error || err?.message || 'Failed to save results';
      showToast.error(errorMessage);
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Header onMenuClick={() => setSidebarOpen(true)} />
      <Box sx={{ display: 'flex' }}>
        <Sidebar
          open={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
          drawerWidth={drawerWidth}
        />
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            p: 0,
            width: { md: `calc(100% - ${drawerWidth}px)` },
            ml: { md: `${0}px` },
            mt: '64px',
            minHeight: 'calc(100vh - 64px)',
            backgroundColor: 'background.default',
          }}
        >
          <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <IconButton onClick={() => navigate('/results')} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Add Results
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            {/* Filters */}
            <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Autocomplete
                  options={classes}
                  getOptionLabel={(option) => option ? `Class ${option.name}${option.section ? ` - ${option.section}` : ''}` : ''}
                  value={classes.find((cls) => cls.id === selectedClass) || null}
                  onChange={(event, newValue) => {
                    setSelectedClass(newValue ? newValue.id : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Select Class *"
                      placeholder="Search and select class..."
                      size="small"
                    />
                  )}
                  sx={{ minWidth: 200 }}
                  ListboxProps={{
                    style: {
                      maxHeight: 200,
                    },
                  }}
                  noOptionsText="No classes found"
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Select Exam *</InputLabel>
                  <Select
                    value={selectedExam}
                    onChange={(e) => setSelectedExam(e.target.value)}
                    label="Select Exam *"
                    disabled={!selectedClass}
                  >
                    <MenuItem value="">
                      <em>Select Exam</em>
                    </MenuItem>
                    {exams.map((exam) => (
                      <MenuItem key={exam.id} value={exam.id}>
                        {exam.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  startIcon={loadingMarks ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
                  onClick={fetchExistingMarks}
                  disabled={!selectedClass || !selectedExam || loadingMarks || loading || students.length === 0}
                  sx={{ minWidth: 180 }}
                >
                  {loadingMarks ? 'Loading...' : 'Load Marks'}
                </Button>
              </Box>
            </Paper>

            {loading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: 24, color: 'text.secondary' }}>
                <CircularProgress size={18} sx={{ mr: 1, color: 'text.secondary', mt: 0.2 }} /> Loading subjects and marks...
              </Box>
            ) : selectedClass && students.length > 0 && subjects.length > 0 ? (
              <>
                <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2, mb: 3 }}>
                  <Table>
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'primary.main', '& th': { fontWeight: 700, color: 'white' } }}>
                        <TableCell>Roll No</TableCell>
                        <TableCell>Student Name</TableCell>
                        {subjects.map((subject) => {
                          // Check if current teacher is assigned to this subject
                          const userId = user?.id || user?.user_id;
                          const isTeacherForSubject = isAdmin() || (isTeacher() && subject.teacher_id === userId);
                          const isRestricted = isTeacher() && !isAdmin() && !isTeacherForSubject;
                          const subjectMax = subjectMaxMarks[subject.id] || defaultMaxMarks;
                          return (
                            <TableCell 
                              key={subject.id} 
                              align="center"
                              sx={{
                                bgcolor: isRestricted ? 'action.disabledBackground' : 'transparent',
                                position: 'relative',
                              }}
                            >
                              <Box sx={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {subject.name}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <Typography variant="caption" sx={{ opacity: isRestricted ? 0.5 : 1, fontSize: '0.7rem' }}>
                                    Max:
                                  </Typography>
                                  <TextField
                                    type="text"
                                    size="small"
                                    value={subjectMax}
                                    onChange={(e) => {
                                      const value = e.target.value;
                                      // Only allow numbers (including empty string)
                                      if (value === '' || /^\d+$/.test(value)) {
                                        const newMaxMarks = { ...subjectMaxMarks };
                                        newMaxMarks[subject.id] = value;
                                        setSubjectMaxMarks(newMaxMarks);
                                        
                                        // Update all students' max marks for this subject
                                        setMarksData((prev) => {
                                          const updated = { ...prev };
                                          Object.keys(updated).forEach((studentId) => {
                                            if (updated[studentId] && updated[studentId][subject.id]) {
                                              updated[studentId][subject.id] = {
                                                ...updated[studentId][subject.id],
                                                maxMarks: value,
                                              };
                                            }
                                          });
                                          return updated;
                                        });
                                      }
                                    }}
                                    placeholder="Max"
                                    sx={{ 
                                      width: 50,
                                      '& .MuiInputBase-root': {
                                        height: '24px',
                                        fontSize: '0.7rem',
                                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                                        '& input': {
                                          padding: '4px 8px',
                                          textAlign: 'center',
                                          color: 'white',
                                        },
                                        '& fieldset': {
                                          borderColor: 'rgba(255, 255, 255, 0.3)',
                                        },
                                        '&:hover fieldset': {
                                          borderColor: 'rgba(255, 255, 255, 0.5)',
                                        },
                                        '&.Mui-focused fieldset': {
                                          borderColor: 'rgba(255, 255, 255, 0.8)',
                                        },
                                      },
                                    }}
                                    disabled={isRestricted}
                                    inputProps={{ 
                                      pattern: '[0-9]*',
                                      inputMode: 'numeric',
                                      style: { color: 'white' }
                                    }}
                                  />
                                </Box>
                                {isRestricted && (
                                  <Tooltip title="You are not assigned to this subject" arrow placement="top">
                                    <LockIcon 
                                      sx={{ 
                                        position: 'absolute',
                                        top: -4,
                                        right: -4,
                                        fontSize: 16,
                                        color: 'error.main',
                                        bgcolor: 'background.paper',
                                        borderRadius: '50%',
                                        p: 0.25,
                                      }} 
                                    />
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {students.map((student, idx) => (
                        <TableRow key={student.id} hover>
                          <TableCell>{student.roll_number}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{student.full_name}</TableCell>
                          {subjects.map((subject) => {
                            const marks = marksData[student.id]?.[subject.id] || { marks: '', maxMarks: '100' };
                            // Check if current teacher is assigned to this subject
                            const userId = user?.id || user?.user_id;
                            const isTeacherForSubject = isAdmin() || (isTeacher() && subject.teacher_id === userId);
                            const isDisabled = isTeacher() && !isAdmin() && !isTeacherForSubject;
                            
                            return (
                              <TableCell 
                                key={subject.id} 
                                align="center"
                                sx={{
                                  bgcolor: isDisabled ? 'action.disabledBackground' : 'transparent',
                                  opacity: isDisabled ? 0.6 : 1,
                                }}
                              >
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center' }}>
                                    <TextField
                                      type="text"
                                      size="small"
                                      value={marks.marks}
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        // Only allow numbers (including empty string)
                                        if (value === '' || /^\d+$/.test(value)) {
                                          handleMarksChange(student.id, subject.id, 'marks', value);
                                        }
                                      }}
                                      placeholder="Marks"
                                      sx={{ width: 80 }}
                                      disabled={isDisabled}
                                      error={!!(validationErrors[student.id]?.[subject.id]?.marks)}
                                      helperText={validationErrors[student.id]?.[subject.id]?.marks || ''}
                                      inputProps={{ 
                                        pattern: '[0-9]*',
                                        inputMode: 'numeric'
                                      }}
                                    />
                                    <Typography variant="body2" sx={{ opacity: isDisabled ? 0.5 : 1 }}>/</Typography>
                                    <TextField
                                      type="text"
                                      size="small"
                                      value={marks.maxMarks}
                                      disabled
                                      onChange={(e) => {
                                        const value = e.target.value;
                                        // Only allow numbers (including empty string)
                                        if (value === '' || /^\d+$/.test(value)) {
                                          handleMarksChange(student.id, subject.id, 'maxMarks', value);
                                        }
                                      }}
                                      placeholder="Max"
                                      sx={{ width: 80 }}
                                      error={!!(validationErrors[student.id]?.[subject.id]?.maxMarks)}
                                      helperText={validationErrors[student.id]?.[subject.id]?.maxMarks || ''}
                                      inputProps={{ 
                                        pattern: '[0-9]*',
                                        inputMode: 'numeric'
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                  <Button variant="outlined" onClick={() => navigate('/results')}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || !selectedClass || !selectedExam}
                  >
                    {saving ? 'Saving...' : 'Save Results'}
                  </Button>
                </Box>
              </>
            ) : selectedClass ? (
              <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  {isTeacher() && !isAdmin() && classes.length === 0
                    ? 'You are not assigned to any subjects in any class. Only assigned teachers can add results.'
                    : 'No students or subjects found for the selected class.'}
                </Typography>
              </Paper>
            ) : (
              <Paper elevation={1} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
                <Typography variant="body1" color="text.secondary">
                  {isTeacher() && !isAdmin() && classes.length === 0
                    ? 'You are not assigned to any subjects in any class. Only assigned teachers can add results.'
                    : 'Please select a class to start adding results.'}
                </Typography>
              </Paper>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default AddResults;

